<template>
  <VBottomSheet
    v-model="showToast"
    :hide-overlay="true"
    :persistent="persistent || null"
  >
    <VSheet
      class="text-center container"
      :class="{ success, error, info, warning }"
    >
      <div class="wrapper">
        <div
          v-if="!custom"
          class="content"
          :class="{
            'align-left': persistent || ctaButton || ctaLink,
            persistent: persistent && !forcedCTA,
          }"
        >
          <div class="toast-simple">
            <span
              v-if="icon && (success || warning || error || info)"
              class="toast-icon"
            >
              <NsIcon :icon-name="statusIcon" size="icon-lg" />
            </span>
            <span class="message">{{ message }}</span>
          </div>
          <div v-if="ctaLink || ctaButton" class="cta">
            <a v-if="ctaLink" class="cta-link" @click="ctaClickHandler">{{
              ctaLink
            }}</a>
            <button
              v-if="ctaButton"
              class="primary-solid short"
              @click="ctaClickHandler"
            >
              {{ ctaButton }}
            </button>
          </div>
          <div
            v-if="ctaList && ctaList.items && ctaList.items.length > 0"
            class="cta list"
          >
            <a @click="onCtaListOptionSelect(ctaList.items[0].id)">{{
              ctaList.items[0].text
            }}</a>

            <span
              ref="chevron"
              class="icon chevron-down"
              @click="showCtaList = true"
            >
              <NsIcon icon-name="icon-chevron-up" size="icon-lg" />
            </span>
            <VBottomSheet
              v-model="showCtaList"
              inset
              persistent
              hide-overlay
              max-width="500"
            >
              <VSheet class="text-center cta-list-container" max-height="90vh">
                <div class="header">
                  <h3>{{ ctaList.title }}</h3>
                  <span class="icon close" @click="onCtaListClose()">
                    <NsIcon icon-name="icon-close" size="icon-sm" />
                  </span>
                  <p>{{ ctaList.subtitle }}</p>
                </div>
                <div class="actions">
                  <a
                    v-for="(item, index) in ctaList.items"
                    :key="index"
                    @click="onCtaListOptionSelect(item.id, true)"
                    >{{ item.text }}</a
                  >
                </div>
              </VSheet>
            </VBottomSheet>
          </div>
        </div>

        <div v-if="custom" class="custom" :class="{ persistent }">
          <!-- @slot Default slot used to provide custom html for the toast -->
          <slot></slot>
        </div>
        <span
          v-if="persistent && !forcedCTA"
          class="icon close"
          @click="onClose()"
        >
          <NsIcon class="chevron-down" icon-name="icon-close" size="icon-lg" />
        </span>
      </div>
    </VSheet>
  </VBottomSheet>
</template>

<script>
import { NsIcon } from '@nuskin/ns-icon';
import { VSheet, VBottomSheet } from 'vuetify/lib';
export default {
  name: 'NsToast',
  components: {
    VBottomSheet,
    VSheet,
    NsIcon,
  },
  props: {
    /**
     * Applies green color to the toast
     */
    success: { type: Boolean, default: false },
    /**
     * Applies red color to the toast
     */
    error: { type: Boolean, default: false },
    /**
     * Applies yellow color to the toast
     */
    info: { type: Boolean, default: false },
    /**
     * Applies yellow color to the toast
     */
    warning: { type: Boolean, default: false },
    /**
     * Adds icon that is relevant to a toast type (success, info, warning, error)
     */
    icon: { type: Boolean, default: false },
    /**
     * Clicking outside of the element will not deactivate it
     */
    persistent: { type: Boolean, default: false },
    /**
     * Toggles only after user selects an action (applicable only for CTA toast)
     */
    forcedCTA: { type: Boolean, default: false },
    /**
     * Allows to use slot with custom html
     */
    custom: { type: Boolean, default: false },
    /**
     * Message inside toast
     */
    message: { type: String, default: '' },
    /**
     * Timout after which toast will deactivate
     */
    delay: { type: Number, default: 5000 },
    /**
     * Call to action link text
     */
    ctaLink: { type: String, default: '' },
    /**
     * Call to action button caption
     */
    ctaButton: { type: String, default: '' },
    /**
     * List of actions. Example:
     * {
     *  title: 'Select Action',
     *  subtitle: 'You can have a subtitle if you'd like.'
     *  items: [{text: 'Primary CTA', id: 'unique-key'}, ...]
     * }
     */
    ctaList: {
      type: Object,
      default: () => {
        return {
          title: '',
          subtitle: '',
          items: [],
        };
      },
    },
    /**
     * Triggers toast activation on value change (Hint: to make toast show do showTrigger = !showTrigger)
     */
    showTrigger: { type: [Boolean, String], default: false },
    /**
     * Fires when any CTA is clicked. Passes id of a clicked cta
     */
    onCtaClick: { type: Function, default: () => {} },
  },
  data() {
    return {
      timeout: null,
      showToast: null,
      showCtaList: null,
    };
  },

  computed: {
    statusIcon() {
      let icon = '';
      switch (true) {
        case this.success:
          icon = 'success';
          break;
        case this.error:
          icon = 'error';
          break;
        case this.warning:
          icon = 'warning';
          break;
        case this.info:
          icon = 'info';
          break;
      }
      return `icon-${icon}-filled`;
    },
  },

  watch: {
    showTrigger() {
      clearTimeout(this.timeout);
      this.showToast = true;
      if (this.persistent) {
        return;
      }
      this.timeout = setTimeout(() => {
        this.showToast = false;
        clearTimeout(this.timeout);
      }, this.delay);
    },
    showCtaList(val) {
      if (val) {
        this.$refs.chevron.className += ' active';
      } else {
        this.$refs.chevron.className = this.$refs.chevron.className
          .split(' active')
          .join('');
      }
    },
  },
  methods: {
    onClose() {
      this.showToast = false;
    },
    onCtaListClose() {
      this.showCtaList = false;
    },
    onCtaListOptionSelect(id) {
      this.showCtaList = false;
      this.showToast = false;
      this.ctaClickHandler(id);
    },
    ctaClickHandler(id) {
      this.onCtaClick(id);
      this.showToast = false;
    },
  },
};
</script>

<style scoped lang="scss">
@use 'sass:map';
@use "~@nuskin/ns-core-styles/src/scss/colors";
$success-alert: map.get(colors.$theme, 'success');
$success-icon: map.get(colors.$theme, 'success');
$success-light-alert: map.get(colors.$theme, 'primary-white');
$warning-alert: map.get(colors.$theme, 'warning');
$warning-icon: map.get(colors.$theme, 'warning');
$warning-light-alert: map.get(colors.$theme, 'primary-white');
$info-alert: map.get(colors.$theme, 'info');
$info-icon: map.get(colors.$theme, 'info');
$info-light-alert: map.get(colors.$theme, 'primary-white');
$error-alert: map.get(colors.$theme, 'error');
$error-icon: map.get(colors.$theme, 'error');
$error-light-alert: map.get(colors.$theme, 'primary-white');

.cta-list-container {
  font-family: proxima-nova;
  height: 100%;
  display: flex;
  flex-direction: column;

  .actions {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    display: block;
    -webkit-clip-path: inset(0 0 0 0 round 10px);
    clip-path: inset(0 0 0 0 round 10px);
    padding-bottom: 100px;
    a {
      padding: 15px;
      width: 100%;
      color: inherit;
      border-bottom: 1px solid #eee;
      font-size: 18px;
      display: inline-block;
    }
  }
  .header {
    position: relative;
    padding: 15px 18px;
    display: flex;
    flex-direction: column;
    align-content: center;
    justify-content: center;
    width: -webkit-fill-available;
    // border-bottom: 1px solid #eee;
    h3 {
      font-weight: bolder;
      text-align: center;
      margin-bottom: 10px;
      font-size: 20px;
    }
    p {
      font-size: 18px;
      text-align: center;
    }
    .close {
      position: absolute;
      top: 12px;
      right: 15px;
      cursor: pointer;
    }
  }
}

.container {
  font-family: proxima-nova;
  padding: 15px 12px;
  font-size: 16px;
  line-height: 18px;
  background-color: white;
  border-top: 3px solid white;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  position: relative;

  .wrapper {
    width: 100%;
    max-width: 500px;
    position: relative;
    display: flex;
    align-items: center;

    .toast-icon {
      margin-right: 10px;
      font-size: 20px;
      align-self: center;
    }
    .close {
      margin: 0 8px 0 20px;
      font-size: 24px;
      cursor: pointer;
    }
    .content {
      display: flex;
      min-width: 100%;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      align-items: center;
      &.align-left {
        justify-content: space-between;
      }
      &.persistent {
        padding-right: 60px;
      }
      .toast-simple {
        display: flex;
        flex-direction: row;
        align-items: center;
        line-height: 1.35em;
        padding-right: 40px;
      }
      .cta {
        position: relative;
        text-align: right;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        a {
          width: 100%;
          text-decoration: underline;
          color: #232121;
          font-weight: bold;
          font-size: 16px;
          white-space: nowrap;
          text-transform: uppercase;
        }
        button {
          white-space: nowrap;
          text-transform: uppercase;
        }
        &.list {
          a {
            text-transform: uppercase;
            text-decoration: none;
            line-height: 1.7em;
            margin-right: 7px;
            border-right: 2px solid rgb(35, 33, 33);
            padding-right: 15px;
          }
          span.icon {
            cursor: pointer;
            font-size: 30px;
            transition: transform 0.2s ease-in;
            &.active {
              transform: rotate(180deg);
            }
          }
        }
      }
    }
    .custom {
      width: 100%;
      &.persistent {
        padding-right: 60px;
      }
    }
  }

  &.success {
    background-color: $success-light-alert !important;
    border-top-color: $success-alert !important;
    .toast-icon {
      color: $success-icon;
    }
  }
  &.error {
    background-color: $error-light-alert !important;
    border-top-color: $error-alert !important;
    .toast-icon {
      color: $error-icon;
    }
  }
  &.warning {
    background-color: $warning-light-alert !important;
    border-top-color: $warning-alert !important;
    .toast-icon {
      color: $warning-icon;
    }
  }
  &.info {
    background-color: $info-light-alert !important;
    border-top-color: $info-alert !important;
    .toast-icon {
      color: $info-icon;
    }
  }
}
@media (max-width: 640px) {
  .container {
    .wrapper {
      flex-direction: column;
      .content {
        flex-direction: column;
        &.persistent {
          padding: 0;
          .message {
            width: 70%;
          }
        }
        .toast-simple {
          padding: 0;
          margin-bottom: 13px;
        }
        .cta {
          width: 100%;
          &.list {
            width: auto;
          }
          a {
            text-align: center;
            &.cta-link {
              padding: 10px 20px;
            }
          }
          button {
            padding: 13px 20px;
            width: 100%;
          }
        }
      }
      .close {
        margin: 0;
      }
    }
  }
}
</style>
